import { useEffect, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { CreditCard, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function StripeDialog({ open, onOpenChange, stripeCfg, onSaved }) {
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [webhookInput, setWebhookInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (open) { setApiKeyInput(""); setWebhookInput(""); setTestResult(null); }
  }, [open]);

  const save = async () => {
    const body = {};
    if (apiKeyInput.trim()) body.api_key = apiKeyInput.trim();
    if (webhookInput.trim()) body.webhook_secret = webhookInput.trim();
    if (!body.api_key && !body.webhook_secret) { toast.error("Enter a key or secret to save."); return; }
    setSaving(true);
    try {
      await api.post("/admin/security/stripe", body);
      toast.success("Stripe settings saved");
      setApiKeyInput(""); setWebhookInput(""); setTestResult(null);
      await onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save Stripe settings");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post("/admin/security/stripe/test");
      setTestResult(res.data);
      if (res.data.ok) toast.success(`Connected to Stripe (${res.data.livemode ? "LIVE" : "TEST"})`);
      else toast.error("Stripe key test failed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not reach Stripe";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CreditCard size={18} className="text-blue-400" /> Stripe settings
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Enter your Stripe keys here to power subscriptions. No server files to edit. Keys are stored securely and only ever shown masked.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-white/10 bg-black/40 p-3 mb-2 space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Secret API key</span>
            {stripeCfg?.api_key_set ? (
              <span className="font-mono text-emerald-400 flex items-center gap-1" data-testid="stripe-apikey-status">
                <CheckCircle size={14} weight="fill" /> {stripeCfg.api_key_mask}
                <span className={`ml-1 text-[10px] uppercase rounded px-1 py-0.5 ${stripeCfg.livemode ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>
                  {stripeCfg.livemode ? "live" : "test"}
                </span>
                <span className="text-[10px] text-zinc-600">({stripeCfg.api_key_source})</span>
              </span>
            ) : (
              <span className="font-mono text-amber-400" data-testid="stripe-apikey-status">not set</span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Webhook signing secret</span>
            {stripeCfg?.webhook_secret_set ? (
              <span className="font-mono text-emerald-400 flex items-center gap-1" data-testid="stripe-webhook-status">
                <CheckCircle size={14} weight="fill" /> {stripeCfg.webhook_secret_mask}
                <span className="text-[10px] text-zinc-600">({stripeCfg.webhook_secret_source})</span>
              </span>
            ) : (
              <span className="font-mono text-amber-400" data-testid="stripe-webhook-status">not set</span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Secret API key (sk_test_… or sk_live_…)</label>
            <Input
              data-testid="stripe-apikey-input"
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder={stripeCfg?.api_key_set ? "Enter a new key to replace" : "sk_test_…"}
              className="bg-black border-zinc-800 focus-visible:ring-blue-600 font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Webhook signing secret (whsec_…)</label>
            <Input
              data-testid="stripe-webhook-input"
              type="password"
              value={webhookInput}
              onChange={(e) => setWebhookInput(e.target.value)}
              placeholder={stripeCfg?.webhook_secret_set ? "Enter a new secret to replace" : "whsec_…"}
              className="bg-black border-zinc-800 focus-visible:ring-blue-600 font-mono"
            />
          </div>
          <div className="rounded-md bg-blue-500/5 border border-blue-500/15 p-2.5 text-xs text-zinc-400">
            In Stripe → Developers → Webhooks, add an endpoint pointing to:
            <code className="block mt-1 font-mono text-blue-300 break-all">{`${API}${stripeCfg?.webhook_url_path || "/webhook/stripe"}`}</code>
            (use your deployed domain, e.g. https://remote.epicom.ca/api/webhook/stripe), then paste its signing secret above.
          </div>
        </div>

        {testResult && (
          <div
            data-testid="stripe-test-result"
            className={`rounded-md border p-2.5 text-xs mt-1 ${testResult.ok ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-300" : "bg-red-500/5 border-red-500/20 text-red-300"}`}
          >
            {testResult.ok ? (
              <div className="flex items-start gap-2">
                <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0" />
                <div>
                  Connected in <span className="font-mono uppercase">{testResult.livemode ? "LIVE" : "TEST"}</span> mode
                  {testResult.business_name ? ` · ${testResult.business_name}` : ""}
                  {testResult.email ? <span className="text-zinc-400"> · {testResult.email}</span> : ""}
                  {testResult.charges_enabled === false && <div className="text-amber-400 mt-0.5">Charges not yet enabled on this account.</div>}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <WarningCircle size={15} weight="fill" className="mt-0.5 shrink-0" />
                <div>{testResult.message || "Key test failed."}</div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-between gap-2">
          <Button
            variant="outline"
            onClick={test}
            disabled={testing || !stripeCfg?.api_key_set}
            className="bg-black border-zinc-800 hover:border-blue-600 hover:text-blue-400 gap-2"
            data-testid="stripe-test"
          >
            <CheckCircle size={15} /> {testing ? "Testing…" : "Test connection"}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/10 hover:bg-zinc-800" data-testid="stripe-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="stripe-save">
              {saving ? "Saving…" : "Save Stripe settings"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
